/**
 * Best-effort, client-side pre-check for Trial Schema image uploads: the server
 * (etmf-server/src/utils/imageQualityValidation.js) always has the final say, but
 * this lets the UI reject obviously colored or blurry images before spending an
 * upload round-trip. Uses the same saturation-ratio / Laplacian-variance heuristics.
 */
const SATURATION_THRESHOLD = 28; // per-pixel max(r,g,b) - min(r,g,b) beyond this counts as "colored"
const MAX_COLOR_PIXEL_RATIO = 0.03; // >3% "colored" pixels => reject
const MIN_SHARPNESS_VARIANCE = 60; // Laplacian variance floor below which the image is treated as blurry
const SAMPLE_MAX_DIMENSION = 500; // downscale before analysis: fast, plenty of resolution for both checks

export function analyzeTrialSchemaImageClientSide(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const scale = Math.min(1, SAMPLE_MAX_DIMENSION / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);

        const gray = new Float64Array(w * h);
        let coloredPixels = 0;
        for (let i = 0, p = 0; i < data.length; i += 4, p++) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          if (Math.max(r, g, b) - Math.min(r, g, b) > SATURATION_THRESHOLD) coloredPixels++;
          gray[p] = 0.299 * r + 0.587 * g + 0.114 * b;
        }
        const colorPixelRatio = coloredPixels / (w * h);

        const at = (x, y) => gray[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
        let sum = 0;
        let sumSq = 0;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const laplacian = at(x - 1, y) + at(x + 1, y) + at(x, y - 1) + at(x, y + 1) - 4 * at(x, y);
            sum += laplacian;
            sumSq += laplacian * laplacian;
          }
        }
        const count = w * h;
        const mean = sum / count;
        const sharpnessVariance = sumSq / count - mean * mean;

        URL.revokeObjectURL(objectUrl);
        resolve({
          colorPixelRatio,
          isColored: colorPixelRatio > MAX_COLOR_PIXEL_RATIO,
          sharpnessVariance,
          isBlurry: sharpnessVariance < MIN_SHARPNESS_VARIANCE,
        });
      } catch (analysisError) {
        URL.revokeObjectURL(objectUrl);
        reject(analysisError);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to read image file'));
    };
    img.src = objectUrl;
  });
}

/** Returns a user-facing rejection message, or '' if the image passes the client-side pre-check. */
export async function getTrialSchemaImageRejectionReason(file) {
  const { isColored, isBlurry } = await analyzeTrialSchemaImageClientSide(file);
  if (!isColored && !isBlurry) return '';
  const issues = [];
  if (isColored) issues.push('contains color');
  if (isBlurry) issues.push('is too blurry/unclear to read');
  return `Trial schema image rejected: the image ${issues.join(' and ')}. Please upload a clear, black-and-white (grayscale/line-art) image.`;
}
